/**
 * RenameModal — Bootstrap modal for renaming a drive folder or file (Preact).
 *
 * Props:
 *   target  {{ type: 'folder'|'file', id, name } | null}
 *   onClose {Function}
 */
import { h } from 'preact'
import { useContext, useEffect, useRef, useState } from 'preact/hooks'
import { Modal } from 'bootstrap'
import { DriveStoreContext, DriveI18nContext } from './drive-context.js'
import { drive_path, drive_rename_file_path } from '../../routes'

export function RenameModal({ target, onClose }) {
  const store = useContext(DriveStoreContext)
  const i18n  = useContext(DriveI18nContext)
  const t     = (key) => (i18n ? i18n.t(key) : key)

  const [name, setName] = useState('')
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const inputRef = useRef(null)
  const modalRef = useRef(null)
  const bsModalRef = useRef(null)

  useEffect(() => {
    if (!modalRef.current) return undefined

    const modal = Modal.getOrCreateInstance(modalRef.current)
    bsModalRef.current = modal

    const onHidden = () => onClose?.()
    modalRef.current.addEventListener('hidden.bs.modal', onHidden)

    return () => {
      modalRef.current?.removeEventListener('hidden.bs.modal', onHidden)
    }
  }, [onClose])

  useEffect(() => {
    if (!target) {
      bsModalRef.current?.hide()
      return
    }

    setName(target.name || '')
    setError(null)
    setSaving(false)
    bsModalRef.current?.show()
    requestAnimationFrame(() => {
      inputRef.current?.focus()
      inputRef.current?.select()
    })
  }, [target])

  async function handleSubmit(e) {
    e.preventDefault()
    if (!target || saving) return

    const trimmed = name.trim()
    if (!trimmed) {
      setError(t('drive.name_required'))
      return
    }

    setSaving(true)
    setError(null)

    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    const isFolder = target.type === 'folder'
    const url = isFolder
      ? drive_path(target.id, { format: 'json' })
      : drive_rename_file_path(target.id, { format: 'json' })
    const body = isFolder
      ? { drive_folder: { name: trimmed } }
      : { filename: trimmed }

    try {
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrf
        },
        body: JSON.stringify(body)
      })

      if (!response.ok) {
        let message = t('drive.error')
        try {
          const data = await response.json()
          if (data.errors?.length) message = data.errors.join(', ')
        } catch { /* ignore */ }
        setError(message)
        setSaving(false)
        return
      }

      const data = await response.json()
      if (isFolder) {
        store.updateFolder(target.id, { name: data.name || trimmed })
      } else {
        store.updateFile(target.id, { filename: data.filename || trimmed })
      }

      bsModalRef.current?.hide()
      onClose?.()
    } catch {
      setError(t('drive.error'))
      setSaving(false)
    }
  }

  const title = target?.type === 'file'
    ? t('drive.rename_file_title')
    : t('drive.rename_folder_title')

  return (
    <div
      class="modal fade"
      id="driveRenameModal"
      tabindex="-1"
      aria-labelledby="driveRenameModalLabel"
      aria-hidden="true"
      ref={modalRef}
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <form onSubmit={handleSubmit}>
            <div class="modal-header">
              <h5 class="modal-title" id="driveRenameModalLabel">
                <i class="bi bi-pencil me-2"></i>{title}
              </h5>
              <button
                type="button"
                class="btn-close"
                data-bs-dismiss="modal"
                aria-label={t('drive.close')}
              />
            </div>
            <div class="modal-body">
              {error && (
                <div class="alert alert-danger py-2">{error}</div>
              )}
              <label class="form-label" for="drive-rename-input">
                {target?.type === 'file' ? t('drive.new_file_name') : t('drive.folder_name')}
              </label>
              <input
                id="drive-rename-input"
                ref={inputRef}
                type="text"
                class="form-control"
                value={name}
                onInput={(e) => setName(e.target.value)}
                disabled={saving}
                autocomplete="off"
              />
            </div>
            <div class="modal-footer">
              <button
                type="button"
                class="btn btn-outline-secondary"
                data-bs-dismiss="modal"
                disabled={saving}
              >
                {t('drive.cancel')}
              </button>
              <button type="submit" class="btn btn-primary" disabled={saving}>
                {saving ? (
                  <span class="spinner-border spinner-border-sm me-1" role="status" />
                ) : (
                  <i class="bi bi-check-lg me-1"></i>
                )}
                {t('drive.rename')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
